import { useEffect, useMemo, useState } from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';

interface DailyDatum { date: string; count: number }

function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * Y-axis width sized to the widest tick label. The old fixed 32px clipped
 * 4-digit labels ("2,460" rendered as "460" — AIT-52). ~7px per glyph at
 * fontSize 12, plus tick padding; never narrower than the original 32px.
 */
export function yAxisWidth(maxY: number): number {
  return Math.max(32, formatCount(maxY).length * 7 + 8);
}

/**
 * One series, named by the org's meter (AIT-436).
 *
 * This was two series and a direction filter. Actions are outbound-only, so
 * the inbound line was a permanent flat zero for every v2 org and the filter
 * had one real setting. With a single line the legend goes too — there is
 * nothing left to tell apart.
 */
export function DailyUsageChart({
  data,
  unitLabel,
  loading = false,
}: {
  data: DailyDatum[];
  unitLabel: 'messages' | 'actions';
  loading?: boolean;
}) {
  // Grow the area up from a flat zero line instead of popping in: render the
  // same dates at value 0 first, then swap to the real values on the next frame
  // so recharts animates the path from the baseline up.
  const zeroed = useMemo(() => data.map((d) => ({ ...d, count: 0 })), [data]);
  // Pin the Y domain to the data's max, not the display's, so the
  // grow-from-zero frame keeps the same axis.
  const maxY = useMemo(() => Math.max(0, ...data.map((d) => d.count)), [data]);
  const [display, setDisplay] = useState<DailyDatum[]>(zeroed);
  const [prevData, setPrevData] = useState(data);
  if (prevData !== data) {
    // Data changed (mount or a range-filter switch). Audit R4-4 (AIT-108):
    // hidden tabs never pump rAF, so the grow-from-zero baseline would stick
    // flat-0 until refocus (forever, to a screenshot/monitor). Nobody sees the
    // animation in a hidden tab — show the real data straight away. Visible
    // tabs get the flat baseline here, then the effect grows it up next frame.
    setPrevData(data);
    setDisplay(document.hidden ? data : zeroed);
  }
  useEffect(() => {
    if (document.hidden) return;
    const id = requestAnimationFrame(() => setDisplay(data));
    return () => cancelAnimationFrame(id);
  }, [data]);

  // Audit R3-11: while the series query is still pending and no data has ever
  // arrived, a flat zero line reads as "no data". Show a skeleton instead.
  // Gating on data-emptiness (mirroring 4dbc57a5's cache-at-mount intent for
  // these non-cached hooks) keeps the previous chart up during filter-switch
  // refetches instead of skeleton-flashing it.
  if (loading && data.length === 0) {
    return (
      <Skeleton
        className="h-[320px] w-full"
        role="status"
        aria-label={`Loading daily ${unitLabel}`}
      />
    );
  }

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={display} aria-label={`Daily ${unitLabel}`} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="fillUsage" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeOpacity={0.15} />
          <XAxis dataKey="date" tickFormatter={formatDay} tickLine={false} axisLine={false} fontSize={12} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={yAxisWidth(maxY)} tickFormatter={formatCount} fontSize={12} domain={[0, maxY > 0 ? maxY : 'auto']} />
          {/* Theme the tooltip explicitly — recharts' default is a hardcoded
              white box, which renders unreadable (white-on-white) under the app
              theme. Bind it to the popover tokens so it's readable either mode. */}
          <Tooltip
            labelFormatter={(l) => formatDay(String(l))}
            formatter={(value: number) => [formatCount(value), unitLabel]}
            contentStyle={{
              background: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--popover-foreground)',
            }}
            labelStyle={{ color: 'var(--popover-foreground)' }}
            itemStyle={{ color: 'var(--popover-foreground)' }}
          />
          <Area
            type="monotone" dataKey="count"
            stroke="var(--primary)" strokeWidth={2} fill="url(#fillUsage)"
            dot={{ r: 2.5, strokeWidth: 0, fill: 'var(--primary)' }}
            activeDot={{ r: 4 }}
            animationDuration={900} animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
